<template>
  <div class="mt-6">
    <div
      v-if="store.isLoading"
      class="text-gray-500"
    >
      Loading projects...
    </div>
    <div
      v-else-if="store.error"
      class="text-red-500"
    >
      Error: {{ store.error }}
    </div>
    <div v-else>
      <div class="mb-4 flex flex-wrap items-end gap-0.5 overflow-hidden">
        <div
          v-for="(status, index) in tabStatuses"
          :key="status.key"
          :class="[
            'relative w-36 -mr-4 last:mr-0',
            index > 0 && status.key !== selectedStatus ? 'shadow-[-4px_0_8px_-6px_rgba(0,0,0,0.35)]' : '',
            status.key === selectedStatus
              ? 'z-20 -ml-2 shadow-[-6px_0_8px_-6px_rgba(0,0,0,0.35),6px_0_8px_-6px_rgba(0,0,0,0.35)]'
              : 'z-0'
          ]"
        >
          <button
            type="button"
            class="w-full h-12 flex items-center justify-center px-3 text-sm font-semibold rounded-t-md transition-colors duration-150 border whitespace-nowrap overflow-hidden text-ellipsis leading-snug"
            :class="status.key === selectedStatus
              ? 'bg-gray-50 text-cerulean border-gray-200 border-b-0'
              : 'bg-gray-50 text-gray-700 border-gray-200 hover:bg-white hover:border-gray-300'"
            @click="selectedStatus = status.key"
          >
            {{ status.label }}
          </button>
        </div>
      </div>

      <div class="flex flex-wrap items-center gap-3 mb-3">
        <label class="text-sm text-gray-700 font-medium flex items-center gap-2">
          <span>Results per page</span>
          <select
            v-model.number="pageSize"
            class="border rounded-md px-2 py-1 text-sm"
          >
            <option
              v-for="size in pageSizeOptions"
              :key="size"
              :value="size"
            >
              {{ size }}
            </option>
          </select>
        </label>
        <div class="text-sm text-gray-600">
          Showing {{ paginatedProjects.length }} of {{ filteredProjects.length }} projects
        </div>
      </div>

      <div
        v-if="!filteredProjects.length"
        class="text-gray-500 text-sm"
      >
        No projects match this status.
      </div>

      <div
        v-else
        class="space-y-6"
      >
        <ProjectTile
          v-for="project in paginatedProjects"
          :key="project.id"
          :project="project"
        />
      </div>

      <div
        v-if="totalPages > 1"
        class="mt-6 flex items-center gap-3 text-sm"
      >
        <button
          type="button"
          class="px-3 py-1.5 rounded-md border bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          :disabled="page === 1"
          @click="page--"
        >
          Previous
        </button>
        <div class="text-gray-700">
          Page {{ page }} of {{ totalPages }}
        </div>
        <button
          type="button"
          class="px-3 py-1.5 rounded-md border bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          :disabled="page === totalPages"
          @click="page++"
        >
          Next
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, onMounted, ref, watch } from 'vue'
import { useProjectManagementStore } from '@/pinia/projectManagementStore'
import ProjectTile from './ProjectTile.vue'

export default {
  components: { ProjectTile },
  setup() {
    const store = useProjectManagementStore()
    onMounted(() => store.fetchProjects())

    // Mapping of status code -> display name (from Accelo job statuses)
    const STATUS_LABELS = {
      8: 'Paused',
      9: 'Complete',
      12: 'Cancelled',
      14: 'Active',
      15: 'Planned',
      16: 'Closing',
      17: 'Upgrade - Test Install',
      18: 'Upgrade - Prod Install',
      19: 'Upgrade QA',
      22: 'Design',
      23: 'Development',
      24: 'UAT',
      25: 'Production Support'
    }

    const STATUS_ORDER = [14, 15, 16, 17, 18, 19, 22, 23, 24, 25, 8, 9, 12]

    const selectedStatus = ref('all')

    const normalizeStatusId = (raw) => {
      if (raw == null) return null
      if (typeof raw === 'object') {
        const value = raw.id ?? raw.Id ?? raw.statusId ?? raw.status_id
        const num = Number(value)
        return Number.isFinite(num) ? num : null
      }
      const num = Number(raw)
      return Number.isFinite(num) ? num : null
    }

    const statusIdForProject = (project) =>
      normalizeStatusId(
        project.status ??
        project.statusId ??
        project.status_id ??
        project.standingId ??
        project.standing_id
      )

    const availableStatusIds = computed(() => {
      const unique = new Set(
        store.projects
          .map(statusIdForProject)
          .filter((id) => id !== null)
      )
      const ordered = STATUS_ORDER.filter((id) => unique.has(id))
      const leftovers = [...unique].filter((id) => !STATUS_ORDER.includes(id)).sort((a, b) => a - b)
      return [...ordered, ...leftovers]
    })

    const tabStatuses = computed(() => [
      { key: 'all', label: 'All' },
      ...availableStatusIds.value.map((id) => ({
        key: id,
        label: STATUS_LABELS[id] || `Status ${id}`
      }))
    ])

    const page = ref(1)
    const pageSize = ref(10)
    const pageSizeOptions = [1, 5, 10, 20, 50, 100]

    const filteredProjects = computed(() => {
      if (selectedStatus.value === 'all') return store.projects
      return store.projects.filter((project) => statusIdForProject(project) === selectedStatus.value)
    })

    const totalPages = computed(() =>
      Math.max(1, Math.ceil((filteredProjects.value.length || 0) / pageSize.value))
    )

    const paginatedProjects = computed(() => {
      const currentPage = Math.min(page.value, totalPages.value)
      const start = (currentPage - 1) * pageSize.value
      return filteredProjects.value.slice(start, start + pageSize.value)
    })

    watch([selectedStatus, pageSize], () => {
      page.value = 1
    })

    watch(totalPages, (newTotal) => {
      if (page.value > newTotal) {
        page.value = newTotal
      }
    })

    return {
      store,
      selectedStatus,
      tabStatuses,
      filteredProjects,
      paginatedProjects,
      page,
      pageSize,
      pageSizeOptions,
      totalPages
    }
  }
}
</script>
